import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { CheckIcon, XIcon, ArrowCounterClockwiseIcon, ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { CourseEvaluatorReviewDecision, CourseEvaluatorReviewSidebarItem } from "../types/reviewTypes";

interface ReviewActionPanelProps {
  selectedReviewItem: CourseEvaluatorReviewSidebarItem | null;
  decision: CourseEvaluatorReviewDecision;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onUndo?: () => void;
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  canAcceptSelectedItem?: boolean;
  acceptDisabledReason?: string;
}

function ReviewActionPanel({
  selectedReviewItem,
  decision,
  onAccept,
  onReject,
  onUndo,
  onNavigateNext,
  onNavigatePrev,
  canAcceptSelectedItem = true,
  acceptDisabledReason,
}: ReviewActionPanelProps) {
  if (!selectedReviewItem) {
    return null;
  }

  const canUndo = !!onUndo;

  if (decision === "synced") {
    return (
      <View style={styles.bar}>
        <View style={styles.leftGroup}>
          <View style={styles.publishedPill}>
            <CustomText
              text="Published"
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={styles.publishedPillText}
            />
          </View>
          <CustomText
            text="Changes have been published. Published items can't be edited."
            useTranslationText={false}
            size="xs"
            style={styles.mutedText}
          />
        </View>
        <NavControls canUndo={canUndo} onUndo={onUndo} onNext={onNavigateNext} onPrev={onNavigatePrev} />
      </View>
    );
  }

  return (
    <View style={styles.bar}>
      <View style={styles.leftGroup}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Add to queue"
          accessibilityState={{ disabled: !canAcceptSelectedItem }}
          onPress={() => onAccept?.(selectedReviewItem)}
          disabled={!canAcceptSelectedItem}
          style={[styles.acceptButton, !canAcceptSelectedItem && styles.acceptButtonDisabled]}>
          <CheckIcon size={14} color={colors.white} weight="bold" />
          <CustomText
            text="Add to Queue"
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={styles.acceptButtonText}
          />
        </Pressable>

        {!canAcceptSelectedItem && acceptDisabledReason && (
          <CustomText text={acceptDisabledReason} useTranslationText={false} size="xs" style={styles.mutedText} />
        )}

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Skip"
          onPress={() => onReject?.(selectedReviewItem)}
          style={styles.skipButton}>
          <XIcon size={14} color={colors.grey[700]} weight="bold" />
          <CustomText text="Skip" useTranslationText={false} size="xs" weight="medium" style={styles.skipButtonText} />
        </Pressable>

        {decision && decision !== "edited" && (
          <View style={[styles.badge, decision === "rejected" ? styles.badgeError : styles.badgeSuccess]}>
            <CustomText
              text={decision === "rejected" ? "Rejected" : "Accepted"}
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={decision === "rejected" ? styles.badgeErrorText : styles.badgeSuccessText}
            />
          </View>
        )}
      </View>

      <NavControls canUndo={canUndo} onUndo={onUndo} onNext={onNavigateNext} onPrev={onNavigatePrev} />
    </View>
  );
}

interface NavControlsProps {
  canUndo: boolean;
  onUndo?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

function NavControls({ canUndo, onUndo, onNext, onPrev }: NavControlsProps) {
  return (
    <View style={styles.rightGroup}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Undo last decision"
        accessibilityState={{ disabled: !canUndo }}
        onPress={onUndo}
        disabled={!canUndo}
        style={[styles.ghostButton, !canUndo && styles.ghostButtonDisabled]}>
        <ArrowCounterClockwiseIcon size={14} color={colors.grey[700]} weight="regular" />
        <CustomText text="Undo" useTranslationText={false} size="xs" weight="medium" style={styles.ghostButtonText} />
      </Pressable>

      <View style={styles.divider} />

      <View style={styles.navGroup}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Navigate to previous item"
          onPress={onPrev}
          style={styles.kbdKey}>
          <ArrowLeftIcon size={12} color={colors.grey[700]} weight="bold" />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Navigate to next item"
          onPress={onNext}
          style={styles.kbdKey}>
          <ArrowRightIcon size={12} color={colors.grey[700]} weight="bold" />
        </Pressable>
        <CustomText text="Navigate" useTranslationText={false} size="xs" style={styles.mutedText} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    height: 44,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: colors.white,
    userSelect: "none",
  },
  leftGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  rightGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  acceptButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: colors.brand[600],
    height: 26,
  },
  acceptButtonText: {
    color: colors.white,
  },
  acceptButtonDisabled: {
    backgroundColor: colors.grey[300],
  },
  skipButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    height: 26,
  },
  skipButtonText: {
    color: colors.grey[700],
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 4,
  },
  badgeSuccess: {
    backgroundColor: colors.success[50],
  },
  badgeError: {
    backgroundColor: colors.error[50],
  },
  publishedPill: {
    paddingHorizontal: 10,
    height: 24,
    justifyContent: "center",
    borderRadius: 9999,
    backgroundColor: "#CCD7FF",
  },
  publishedPillText: {
    color: "#000B33",
  },
  badgeSuccessText: {
    color: colors.success[700],
  },
  badgeErrorText: {
    color: colors.error[700],
  },
  ghostButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  ghostButtonDisabled: {
    opacity: 0.5,
  },
  ghostButtonText: {
    color: colors.grey[700],
  },
  divider: {
    width: 1,
    height: 16,
    backgroundColor: colors.grey[200],
  },
  navGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  kbdKey: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 4,
  },
  mutedText: {
    color: colors.grey[500],
  },
});

export default ReviewActionPanel;
